/**
 * Cross-product "Workspaces" switcher configuration.
 *
 * Defines the three GraymatterLab ecosystem surfaces shown in the header
 * switcher (see docs/workspace-switcher-spec.md). Every product lists all
 * three — including itself. This app is **Agent Designer**, so that row is
 * rendered as the non-interactive "current" entry; the other two open in a
 * new tab.
 *
 * Destination URLs are driven from env (with production defaults) so each
 * environment (local/staging/prod) resolves correctly — see §7.
 */
import React from 'react';
import {
  AcademicCapIcon,
  PencilIcon,
  RocketLaunchIcon,
  type IconProps,
} from '../components/ui/Icons';

// CURRENT is the only badge in active use; New/Pro palettes are preserved
// in BADGE_CLASSES for optional future use (spec §6).
export type WorkspaceBadge = 'CURRENT' | 'New' | 'Pro';

export interface Workspace {
  id: string;
  name: string;
  description: string;
  icon: React.FC<IconProps>;
  /**
   * Absolute destination URL. Defined for every surface (kept symmetric),
   * though it is unused for the current product's own non-interactive row.
   */
  url: string;
  /** True for the product this app represents (Agent Designer). */
  isCurrent: boolean;
}

const env = import.meta.env;

// Each surface resolves from env with a production default.
const STUDIO_URL = env.VITE_STUDIO_URL || 'https://studio.graymatterlab.ai';
const HUB_URL = env.VITE_HUB_URL || 'https://learn.graymatterlab.ai';
const DESIGNER_URL = env.VITE_DESIGNER_URL || 'https://designer.graymatterlab.ai';

/** Badge styling — shared shape, per-badge palette (spec §6). */
export const BADGE_CLASSES: Record<WorkspaceBadge, string> = {
  CURRENT: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
  New: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300',
  Pro: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
};

/** Fixed order per spec §5. */
export const WORKSPACES: Workspace[] = [
  {
    id: 'agent-studio',
    name: 'Agent Studio',
    description: 'Build & operate agents',
    icon: RocketLaunchIcon,
    url: STUDIO_URL,
    isCurrent: false,
  },
  {
    id: 'agent-hub',
    name: 'Agent Hub',
    description: 'Learn & train',
    icon: AcademicCapIcon,
    url: HUB_URL,
    isCurrent: false,
  },
  {
    id: 'agent-designer',
    name: 'Agent Designer',
    description: 'Design agents visually',
    icon: PencilIcon,
    url: DESIGNER_URL,
    isCurrent: true,
  },
];
